import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../lib/utils";
import { format } from "date-fns";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
  Chart,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import DateRangePickerButton from "../../../components/DateRangePicker/DateRangePickerButton";
import { IDateRangePreset } from "../../../interfaces";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

interface ChartDataPoint {
  date: string;
  totalCost_with_tax: number;
  estimateRevenue: number;
  totalNet_with_tax: number;
  iapRevenue: number;
} 

interface AppRevenueChartProps {
  data: ChartDataPoint[];
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
  loading: boolean;
  setSelectedPresetForChart: (preset: IDateRangePreset | undefined) => void;
  selectedPresetForChart?: IDateRangePreset;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

const AppRevenueChart = ({
  data,
  onDateRangeChange,
  loading,
  setSelectedPresetForChart,
  dateRange,
  selectedPresetForChart,
}: AppRevenueChartProps) => {
  const { t } = useTranslation();

  const chartRef = useRef<Chart<"line"> | null>(null);

  // Prepare chart data for Chart.js
  const chartData: ChartData<"line"> = {
    labels: data.map((d) => d.date),
    datasets: [
      {
        label: t("apps.columns.estimatedRevenue"),
        data: data.map((d) => d.estimateRevenue),
        borderColor: "#22c55e",
        fill: true,
        tension: 0.5,
      },
      {
        label: t("dashboard.summary.iapRevenue"),
        data: data.map((d) => d.iapRevenue),
        borderColor: "#f59e0b",
        fill: true,
        tension: 0.5,
      },
      {
        label: t("apps.columns.totalCost"),
        data: data.map((d) => d.totalCost_with_tax),
        borderColor: "#ef4444",
        fill: true,
        tension: 0.5,
      },
      {
        label: t("dashboard.summary.totalNet"),
        data: data.map((d) => d.totalNet_with_tax),
        borderColor: "#91bbff",
        fill: true,
        tension: 0.5,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
            weight: "bold",
          },
          color: "var(--text-color)",
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 12,
          padding: 16,
        },
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        displayColors: false,
        usePointStyle: true,
        callbacks: {
          title: function (items) {
            return `${t("charts.axes.date")}: ${format(
              new Date(items[0].label as string),
              "yyyy-MM-dd"
            )}`;
          },
          label: function (context) {
            return `${context.dataset.label} : ${formatCurrency(
              context.raw as number,
              "USD"
            )}`;
          },
          labelTextColor: function (context) {
            return (context.dataset.borderColor as string) || "#222";
          },
        },
        backgroundColor: "#fff",
        titleColor: "#222",
        bodyColor: "#222",
        borderColor: "#e5e7eb",
        borderWidth: 2,
        padding: 12,
        boxPadding: 6,
        titleMarginBottom: 10,
        bodySpacing: 10,
        titleFont: {
          size: 15,
          weight: "bolder",
        },
        bodyFont: {
          size: 14,
          weight: "normal",
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "x",
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          // drag: {
          //   enabled: false,
          // },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 18, // Increased for better zoom granularity
          font: {
            family: "var(--font-inter)",
            size: 11,
          },
          color: "#6b7280",
          callback: function (_value, index) {
            const label = this.getLabelForValue(index);
            if (label && typeof label === 'string') {
              const datePart = label.includes('T') ? label.split('T')[0] : label;
              const parts = datePart.split('-');
              if (parts.length === 3) {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const day = parts[2];
                const month = months[parseInt(parts[1]) - 1];
                return `${day} ${month}`;
              }
            }
            return label;
          }
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: "var(--font-inter)",
            size: 12,
          },
          color: "#6b7280",
          callback: function (tickValue: number | string) {
            if (typeof tickValue === "number") {
              return formatCurrency(tickValue, "USD");
            }
            return tickValue;
          },
        },
      },
    },
  };

  useEffect(() => {
    if (chartRef.current) {
      const setGradients = (
        chartInstance: Chart<"line"> | null,
        stops: [string, string][]
      ) => {
        if (!chartInstance) return;
        const { ctx, chartArea } = chartInstance;
        if (!chartArea) return;

        const height = chartArea.bottom;
        stops.forEach(([start, end], index) => {
          const gradient = ctx.createLinearGradient(0, 0, 0, height);
          gradient.addColorStop(0, start);
          gradient.addColorStop(1, end);
          chartInstance.data.datasets[index].backgroundColor = gradient;
        });

        chartInstance.update();
      };

      setTimeout(() => {
        setGradients(chartRef.current, [
          ["rgba(34, 197, 94, 0.4)", "rgba(34, 197, 94, 0)"],
          ["rgba(245, 158, 11, 0.25)", "rgba(245, 158, 11, 0)"],
          ["rgba(239, 68, 68, 0.2)", "rgba(239, 68, 68, 0)"],
          ["rgba(145, 187, 255, 0.4)", "rgba(145, 187, 255, 0)"],
        ]);
      }, 100);
    }
  }, [data]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <h5 className="text-xl font-bold text-gray-700">
            {`${t("apps.columns.estimatedRevenue")} vs ${t(
              "apps.columns.totalCost"
            )}`}
          </h5>
        </div>
        <DateRangePickerButton
          onDateRangeChange={onDateRangeChange}
          onPresetChange={setSelectedPresetForChart}
          initialPreset={selectedPresetForChart?.id}
          initialEndDate={dateRange.endDate}
          initialStartDate={dateRange.startDate}
        />
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-[calc(100vh-400px)]">
          <div className="w-full h-full rounded bg-gray-200 animate-pulse my-2"></div>
        </div>
      ) : (
        <div className="h-[calc(100vh-400px)]">
          <Line
            ref={chartRef}
            data={chartData}
            options={options}
            height={320}
          />
        </div>
      )}
    </div>
  );
};

export default AppRevenueChart;
